/**
 * Property overview gallery: ordered URLs for header / lightbox.
 * Cover photo first; then Airbnb scrape URLs (deduped).
 */

import { supabase } from "@/integrations/supabase/client";

function normalizeUrl(url: string): string {
  return url.trim();
}

/** Build ordered gallery URLs: cover first, then scrape URLs in order (no duplicates). */
export function buildPropertyGalleryUrls(
  coverPhotoUrl: string | null | undefined,
  scrapeUrls: string[]
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  const push = (url: string | null | undefined) => {
    if (!url) return;
    const n = normalizeUrl(url);
    if (!n || seen.has(n)) return;
    seen.add(n);
    out.push(n);
  };

  push(coverPhotoUrl);
  for (const url of scrapeUrls) push(url);

  return out;
}

/** Move chosen URL to the front (cover) while keeping the rest in order. */
export function reorderGalleryWithNewCover(photos: string[], newCoverUrl: string): string[] {
  return buildPropertyGalleryUrls(newCoverUrl, photos);
}

/** Persist gallery on properties (property_photo_urls + property_photo_url). */
export async function persistPropertyGalleryPhotos(
  propertyId: string,
  urls: string[]
): Promise<void> {
  const gallery = urls.map(normalizeUrl).filter(Boolean);
  const primary = gallery[0] ?? null;

  const { error } = await supabase
    .from("properties")
    .update({
      property_photo_url: primary,
      property_photo_urls: gallery,
    })
    .eq("id", propertyId);

  if (error) {
    if (error.code === "42703" || String(error.message).includes("property_photo_urls")) {
      if (primary) {
        await supabase
          .from("properties")
          .update({ property_photo_url: primary })
          .eq("id", propertyId);
      }
      return;
    }
    throw error;
  }
}

/** After reference import, refresh gallery from stored reference photo URLs (cover stays first). */
export async function syncPropertyGalleryFromReferencePhotos(
  propertyId: string,
  coverPhotoUrl: string | null | undefined
): Promise<void> {
  const { data, error } = await supabase
    .from("reference_photos")
    .select("photo_url, display_order, created_at")
    .eq("property_id", propertyId)
    .is("checklist_id", null)
    .order("display_order", { ascending: true })
    .order("created_at", { ascending: true });

  if (error) throw error;

  const refUrls = (data ?? []).map((r) => r.photo_url).filter(Boolean) as string[];
  if (refUrls.length === 0) return;

  const gallery = buildPropertyGalleryUrls(coverPhotoUrl, refUrls);
  await persistPropertyGalleryPhotos(propertyId, gallery);
}
